import React, { useCallback, useState } from 'react'
import { Tooltip } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons'
import { faEye } from '@fortawesome/pro-solid-svg-icons/faEye'
import { useTranslation } from 'react-i18next'

import { dc3AttestationsService } from 'api/administration/dc3Attestations'
import {
  Dc3CertificateGenerateModal,
  mapAttestationDetailToPreview,
} from 'components/dc3Certificate'
import { useNotification } from 'hooks/notifications/useNotification'

import {
  Label,
  StatusRow,
  StatusText,
  TooltipTitle,
  ViewButton,
  Wrapper,
} from './styled'

interface LegalAttestationSectionProps {
  assigned: boolean
  attestationId: number | null
  courseName: string
  courseHours?: number | null
  /** En ficha de curso: el título lo renderiza la grilla de metadatos. */
  inline?: boolean
}

const LegalAttestationSection: React.FC<LegalAttestationSectionProps> = ({
  assigned,
  attestationId,
  courseName,
  courseHours,
  inline = false,
}) => {
  const { t } = useTranslation()
  const notification = useNotification()
  const prefix = 'monitoring.course.legalAttestation'

  const [previewModalOpen, setPreviewModalOpen] = useState(false)
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [previewConfig, setPreviewConfig] = useState<{
    attestationId: number
    attestation: ReturnType<typeof mapAttestationDetailToPreview>
    course: { name: string; hours: number }
  } | null>(null)

  const handleViewAttestation = useCallback(async () => {
    if (!attestationId) return

    setLoadingPreview(true)

    try {
      const detail = await dc3AttestationsService.getDc3Attestation(
        attestationId
      )

      setPreviewConfig({
        attestationId: detail.id,
        attestation: mapAttestationDetailToPreview(detail),
        course: {
          name: courseName,
          hours: Number(courseHours) || 0,
        },
      })
      setPreviewModalOpen(true)
    } catch {
      notification.error(t(`${prefix}.previewError`))
    } finally {
      setLoadingPreview(false)
    }
  }, [attestationId, courseHours, courseName, notification, t])

  const handleClosePreview = useCallback(() => {
    setPreviewModalOpen(false)
    setPreviewConfig(null)
  }, [])

  return (
    <Wrapper>
      {!inline ? <Label>{t(`${prefix}.label`)}</Label> : null}

      {assigned ? (
        <ViewButton
          type="button"
          $inline={inline}
          onClick={handleViewAttestation}
          disabled={loadingPreview || !attestationId}
        >
          {t(`${prefix}.viewAttestation`)}
          <FontAwesomeIcon icon={faEye} />
        </ViewButton>
      ) : (
        <Tooltip
          title={<TooltipTitle>{t(`${prefix}.tooltip`)}</TooltipTitle>}
          placement="top"
        >
          <StatusRow>
            <FontAwesomeIcon icon={faTriangleExclamation} />
            <StatusText>{t(`${prefix}.missingStatus`)}</StatusText>
          </StatusRow>
        </Tooltip>
      )}

      {previewConfig ? (
        <Dc3CertificateGenerateModal
          open={previewModalOpen}
          mode="preview"
          previewConfig={previewConfig}
          onClose={handleClosePreview}
        />
      ) : null}
    </Wrapper>
  )
}

export default LegalAttestationSection
